import React, { useRef, useState, useEffect } from 'react';
import ReactDiffViewer, {
  DiffMethod,
  ReactDiffViewerStylesOverride,
} from 'react-diff-viewer-continued';

interface JsonDiffProps {
  old?: Record<string, unknown>;
  new?: Record<string, unknown>;
  leftTitle?: string;
  rightTitle?: string;
}

function serializeForDiff(value?: Record<string, unknown>): string {
  if (!value) {
    return '';
  }

  return JSON.stringify(value, null, 2)
    .split('\n')
    .map((line) => line.replace(/,\s*$/, ''))
    .join('\n');
}

const SPLIT_VIEW_MIN_WIDTH = 900;

const githubDiffStyles: ReactDiffViewerStylesOverride = {
  variables: {
    light: {
      diffViewerBackground: '#ffffff',
      diffViewerTitleBackground: '#f6f8fa',
      diffViewerTitleColor: '#24292f',
      diffViewerTitleBorderColor: '#d0d7de',
      diffViewerColor: '#24292f',
      addedBackground: '#dafbe1',
      addedColor: '#24292f',
      removedBackground: '#ffebe9',
      removedColor: '#24292f',
      changedBackground: '#fff8c5',
      wordAddedBackground: '#aceebb',
      wordRemovedBackground: '#ffcecb',
      addedGutterBackground: '#ccffd8',
      removedGutterBackground: '#ffd8d3',
      gutterBackground: '#f6f8fa',
      highlightBackground: '#fff8c5',
      highlightGutterBackground: '#fff1b8',
      gutterColor: '#57606a',
      addedGutterColor: '#1a7f37',
      removedGutterColor: '#cf222e',
      codeFoldBackground: '#f6f8fa',
      codeFoldContentColor: '#57606a',
      emptyLineBackground: '#ffffff',
      codeFoldGutterBackground: '#f6f8fa',
    },
  },
  diffContainer: {
    border: '1px solid #d0d7de',
    borderRadius: 6,
    overflow: 'hidden',
    minWidth: 'unset',
    'col:nth-child(1), col:nth-child(4)': {
      width: '24px !important',
    },
    'col:nth-child(2), col:nth-child(5)': {
      width: '0px !important',
    },
  },
  titleBlock: {
    borderBottom: '1px solid #d0d7de',
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
  },
  contentText: {
    fontFamily:
      'ui-monospace, SFMono-Regular, SF Mono, Menlo, Consolas, Liberation Mono, monospace',
    fontSize: '0.75rem',
    lineHeight: '20px',
    lineBreak: 'auto',
  },
  marker: {
    opacity: '0 !important',
  },
  gutter: {
    minWidth: '24px !important',
    width: '24px !important',
    padding: '0 2px',
  },
  lineNumber: {
    display: 'none',
  },
};

const CONTEXT_LINES = 3;

const JsonDiff: React.FC<JsonDiffProps> = ({ old: oldVal, new: newVal }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [splitView, setSplitView] = useState(true);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      setSplitView(entry.contentRect.width >= SPLIT_VIEW_MIN_WIDTH);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const oldJson = serializeForDiff(oldVal);
  const newJson = serializeForDiff(newVal);

  if (!oldJson && !newJson) {
    return null;
  }

  return (
    <div ref={containerRef} className="json-diff-wrapper">
      <ReactDiffViewer
        oldValue={oldJson}
        newValue={newJson}
        splitView={splitView}
        hideLineNumbers={false}
        hideSummary={true}
        compareMethod={DiffMethod.WORDS}
        showDiffOnly={true}
        extraLinesSurroundingDiff={CONTEXT_LINES}
        styles={githubDiffStyles}
        codeFoldMessageRenderer={() => <></>}
      />
    </div>
  );
};

export default JsonDiff;
